import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Star, ExternalLink } from "lucide-react";

const REVIEW_LINK = "https://search.google.com/local/writereview?placeid=ChIJPyKx0dRzbqoRfJpak9spwbM";

const Stars = ({ n }) => (
  <div className="flex gap-1">
    {Array.from({ length: Math.round(n || 0) }).map((_, i) => (
      <Star key={`s-${i}`} className="h-4 w-4 fill-brand-red text-brand-red" />
    ))}
  </div>
);

export function GoogleRatingBadge({ className = "" }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/reviews").then((r) => setData(r.data)).catch(() => {});
  }, []);
  if (!data?.rating) return null;
  return (
    <a
      data-testid="google-rating-inline"
      href={data.maps_uri || "https://maps.google.com"}
      target="_blank"
      rel="noreferrer"
      className={`inline-flex items-center gap-2.5 px-4 py-2.5 transition-opacity hover:opacity-85 ${className}`}
      style={{ backgroundColor: "#0B0B0C", border: "1px solid #3f3f46" }}
    >
      <span className="font-display text-xl !text-white leading-none" style={{ color: "#fff" }}>{data.rating.toFixed(1)}</span>
      <Stars n={data.rating} />
      <span className="font-mono text-[10px] uppercase tracking-widest" style={{ color: "#d4d4d8" }}>
        {data.total} Google reviews
      </span>
    </a>
  );
}

export function GoogleReviews({ testimonials = [] }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/reviews").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const live = data?.reviews?.length > 0;

  return (
    <div data-testid="review-wall">
      {live && (
        <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3" data-testid="google-rating-badge">
          <div className="flex items-center gap-3">
            <span className="font-display text-5xl text-white">{data.rating?.toFixed(1)}</span>
            <div>
              <Stars n={data.rating} />
              <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                {data.total} reviews on Google
              </p>
            </div>
          </div>
          <div className="flex gap-3 ml-auto">
            <a
              data-testid="leave-review-btn"
              href={REVIEW_LINK}
              target="_blank"
              rel="noreferrer"
              className="px-5 py-2.5 bg-brand-red text-white text-xs font-mono uppercase tracking-widest hover:bg-[#B91C2B] transition-colors"
            >
              Leave a review
            </a>
            {data.maps_uri && (
              <a
                data-testid="see-all-reviews-link"
                href={data.maps_uri}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 border border-zinc-700 text-zinc-300 text-xs font-mono uppercase tracking-widest hover:border-brand-red hover:text-brand-red transition-colors"
              >
                See all on Google <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}
          </div>
        </div>
      )}

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-900 border border-zinc-900">
        {live
          ? data.reviews.slice(0, 4).map((r, i) => (
              <article key={`g-${i}`} data-testid={`google-review-${i}`} className="bg-zinc-950 p-7 flex flex-col">
                <Stars n={r.rating} />
                <p className="mt-5 text-zinc-300 leading-relaxed text-sm flex-1 line-clamp-6">&ldquo;{r.text}&rdquo;</p>
                <div className="mt-6 pt-5 border-t border-zinc-900 flex items-center gap-3">
                  {r.photo && <img src={r.photo} alt="" loading="lazy" referrerPolicy="no-referrer" className="h-9 w-9 rounded-full" />}
                  <div className="min-w-0">
                    <a href={r.author_uri || data.maps_uri} target="_blank" rel="noreferrer" className="block font-display uppercase text-base text-white tracking-tight truncate hover:text-brand-red transition-colors">
                      {r.author}
                    </a>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{r.when} · Google</div>
                  </div>
                </div>
              </article>
            ))
          : testimonials.map((t) => (
              <article key={t.id} data-testid={`testimonial-${t.id}`} className="bg-zinc-950 p-7 flex flex-col">
                <Stars n={t.rating} />
                <p className="mt-5 text-zinc-300 leading-relaxed text-sm flex-1">&ldquo;{t.text}&rdquo;</p>
                <div className="mt-6 pt-5 border-t border-zinc-900">
                  <div className="font-display uppercase text-base text-white tracking-tight">{t.name}</div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{t.location}</div>
                </div>
              </article>
            ))}
      </div>
      {live && (
        <p className="mt-3 text-[11px] text-zinc-600">Reviews and ratings provided by Google. Not all reviews are shown.</p>
      )}
    </div>
  );
}
